import { NumberFormatter } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { Minus, Plus, X } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  useDeleteCartItemMutation,
  useGetAllCartWithIdQuery,
  useUpdateItemQuantityMutation,
} from "Services/CartApiSlice";
import {
  calculateDiscount,
  calculateSubTotal,
  removeFromCart,
  removeFromProductCart,
  setInteracting,
  updateQuantity,
} from "State/CartSlice/CartSlice";
import { RootState } from "State/store";
import { ProductMetaDTO } from "Types/ProductMetaDTO";
import { useEcomConfig } from "hooks/useEcomConfig";
import { getBaseCurrency } from "Utilities/CurrencyHandler";
import { encodeParam } from "Utilities/urlEncoder";

const CartItemImage = ({ src, alt, className }: { src?: string, alt?: string, className?: string }) => {
  const [error, setError] = useState(false);
  if (error || !src) {
    return (
      <div className={`bg-gray-100 flex items-center justify-center rounded border border-vintageBorder text-center p-1 overflow-hidden ${className}`}>
        <span className="text-[10px] lg:text-xs text-gray-500 leading-tight block w-full whitespace-normal">No Image Available</span>
      </div>
    );
  }
  return <img src={src} alt={alt} className={className} onError={() => setError(true)} />;
};

interface CartItemCardProps {
  item: ProductMetaDTO;
  quantity: number;
  token: string | null;
  showQuantitySelector?: boolean;
  showDeleteIcon?: boolean;
  byQuantityPromotionProducts?: Array<{ id: string; qty: number }>;
  onClose?: () => void;
  availableQty?: number;
  skipQuery?: boolean;
}

const CartItemCard: React.FC<CartItemCardProps> = ({
  item,
  quantity: initialQuantity,
  token,
  showQuantitySelector = false,
  showDeleteIcon = false,
  byQuantityPromotionProducts = [],
  onClose,
  availableQty: propAvailableQty,
  skipQuery = false,
}) => {
  const dispatch = useDispatch();
  const [deleteCartItem, { isLoading: isDeleting }] = useDeleteCartItemMutation();
  const [updateCartItemQuantity] = useUpdateItemQuantityMutation();
  const [quantity, setQuantity] = useState(initialQuantity);
  const [isQuantityExceeded, setIsQuantityExceeded] = useState(false);
  const [byQuantityPromoProducts, setByQuantityPromoProducts] = useState<
    ProductMetaDTO[]
  >([]);
  const [prevPromoIds, setPrevPromoIds] = useState<string>("");

  const [isIncreasing, setIsIncreasing] = useState(false);
  const [isDecreasing, setIsDecreasing] = useState(false);
  const [isOfferLoading, setIsOfferLoading] = useState(false);

  const { allowLTAVAILINVQTY, numberOfDecimal, showPromotion, maxCartQty, minInvQtyPerProduct } = useEcomConfig();

  const hasBuiltInAvailQty = item.product.availqty != null;
  const isStockLoading = propAvailableQty === undefined && !hasBuiltInAvailQty;
  const stockQty =
    propAvailableQty !== undefined
      ? propAvailableQty
      : hasBuiltInAvailQty
      ? item.product.availqty ?? 0
      : 0; // Removed individual fallback query
  const activePromotions = useMemo(
    () => (showPromotion ? item.promotions || [] : []),
    [showPromotion, item.promotions],
  );

  const { cartList = [], isInteracting = false } = useSelector((state: RootState) => state.cart || {});

  const cartItem = cartList.find(
    (cartItem: any) => cartItem.productId === String(item.product.item),
  );
  const actualSize = cartItem?.size || "";

  const getByValuePromotionView = useCallback((promotions: any[]) => {
    if (!showPromotion) return "";

    const byValuePromos =
      promotions?.filter((p) => p.promotionBy === "ByValue") || [];

    const messages = byValuePromos
      .filter((p) => p.buyQty && p.buyQty >= 1)
      .map(
        (p) =>
          `Add ${p.buyQty} quantities to unlock the offer: ${p.promotionName}`,
      );

    return messages.join(" | ");
  }, [showPromotion]);

  const promoIds = useMemo(() => {
    return byQuantityPromotionProducts.map((p) => p.id).sort();
  }, [byQuantityPromotionProducts]);

  const { data: promoProductsResult, isFetching: isOfferLoadingQuery } = useGetAllCartWithIdQuery(
    { cartItems: promoIds },
    { skip: skipQuery || !showPromotion || promoIds.length === 0 }
  );

  useEffect(() => {
    setIsOfferLoading(isOfferLoadingQuery);
  }, [isOfferLoadingQuery]);

  useEffect(() => {
    if (promoProductsResult?.products) {
      setByQuantityPromoProducts(promoProductsResult.products);
    } else {
      setByQuantityPromoProducts([]);
    }
  }, [promoProductsResult]);

  useEffect(() => {
    setQuantity(initialQuantity);
  }, [initialQuantity]);

  const id = item.product.item;
  const title = item.product.itemDesc;
  const author = item.product.author || "";
  const image = item.imagePath;

  const isOutOfStock = useMemo(
    () => !isStockLoading && allowLTAVAILINVQTY === 0 && stockQty === 0,
    [isStockLoading, allowLTAVAILINVQTY, stockQty],
  );
  const isCartMaxed = useMemo(
    () =>
      (allowLTAVAILINVQTY === 0 && stockQty > 0 && quantity >= stockQty) ||
      (allowLTAVAILINVQTY === 0 && isFinite(maxCartQty) && quantity >= maxCartQty),
    [allowLTAVAILINVQTY, stockQty, quantity, maxCartQty],
  );
  const increaseQuantity = useCallback(async () => {
    if (isIncreasing || isOfferLoading || isInteracting) return;

    if (isOutOfStock) {
      return;
    }

    const newQuantity = quantity + 1;

    if (newQuantity > maxCartQty) {
      setIsQuantityExceeded(true);
      return;
    }

    if (allowLTAVAILINVQTY === 0 && stockQty > 0 && newQuantity > stockQty) {
      setIsQuantityExceeded(true);
      return;
    }

    setIsIncreasing(true);
    setQuantity(newQuantity);
    setIsQuantityExceeded(false);

    dispatch(
      updateQuantity({
        productId: item.product.item,
        quantity: newQuantity,
        size: actualSize,
      }),
    );

    try {
      if (token) {
        dispatch(setInteracting(true));
        await updateCartItemQuantity({
          productId: item.product.item,
          mode: "increment",
          size: actualSize,
        }).unwrap();
      }
    } catch (err) {
      console.error("Failed to update quantity:", err);
    } finally {
      setIsIncreasing(false);
      dispatch(setInteracting(false));
    }

    dispatch(calculateSubTotal());
    dispatch(calculateDiscount());
  }, [
    quantity,
    stockQty,
    maxCartQty,
    allowLTAVAILINVQTY,
    token,
    item.product.item,
    actualSize,
    dispatch,
    updateCartItemQuantity,
    isIncreasing,
    isOfferLoading,
    isOutOfStock,
    isInteracting,
    item.product.itemDesc,
  ]);

  const decreaseQuantity = async () => {
    if (isDecreasing || isIncreasing || quantity <= 1 || isOfferLoading || isInteracting) return;

    setIsDecreasing(true);
    const newQuantity = quantity - 1;

    setQuantity(newQuantity);
    setIsQuantityExceeded(false);

    dispatch(
      updateQuantity({
        productId: item.product.item,
        quantity: newQuantity,
        size: actualSize,
      }),
    );

    try {
      if (token) {
        dispatch(setInteracting(true));
        await updateCartItemQuantity({
          productId: item.product.item,
          mode: "decrement",
          size: actualSize,
        }).unwrap();
      }
    } catch (err) {
      console.error("Failed to decrement quantity:", err);
    } finally {
      setIsDecreasing(false);
      dispatch(setInteracting(false));
    }

    dispatch(calculateSubTotal());
    dispatch(calculateDiscount());
  };

  const isRemovingRef = useRef(false);
  const [isInitiated, setIsInitiated] = useState(false);

  const removeItem = async () => {
    if (isRemovingRef.current || isInteracting) return;
    isRemovingRef.current = true;
    setIsInitiated(true);

    const removedProductId = item.product.item;
    const removedSize = actualSize;

    // Set global interaction lock
    dispatch(setInteracting(true));

    // Optimistically remove from Redux state FIRST
    // This causes the component to unmount instantly, preventing any further clicks
    dispatch(removeFromCart({ productId: removedProductId, size: removedSize }));
    dispatch(removeFromProductCart(removedProductId));

    if (token) {
      try {
        // Call API in the background
        const res = await deleteCartItem({
          productid: removedProductId,
          size: removedSize,
        }).unwrap();
        notifications.show({ message: res.message, color: "red" });
      } catch (err: any) {
        console.error("Background cart removal failed:", err);
        notifications.show({ message: err?.data?.message ?? "Failed to remove item", color: "red" });
      } finally {
        // Release global lock
        dispatch(setInteracting(false));
      }
    } else {
      // Re-re-release lock if no token was present
      dispatch(setInteracting(false));
    }

  };

  //#region Calculate Discounted Price (EXACTLY SAME AS ProductInfoSidebar)
  const calculateDiscountedPrice = useCallback(() => {
    if (!showPromotion) return Number(item.product.ecomUnitPrice) || 0;

    const basePrice = Number(item.product.ecomUnitPrice) || 0;

    const applicablePromos = activePromotions.filter(
      (p: any) =>
        p.promotionBy === "ByValue" &&
        typeof p.buyQty === "number" &&
        quantity >= p.buyQty,
    );

    if (!applicablePromos.length) return basePrice;

    let price = basePrice;

    for (const promo of applicablePromos) {
      if (promo.promotionType === "%") {
        price -= (basePrice * promo.promotion) / 100;
      }
      if (promo.promotionType === "INR") {
        price -= promo.promotion;
      }
    }

    return Math.max(price, 0);
  }, [item.product.ecomUnitPrice, activePromotions, quantity, showPromotion]);

  //#endregion

  //#region Calculate Discount Percentage (EXACTLY SAME AS ProductInfoSidebar)
  const calculateDiscountedPercentage = useCallback(() => {
    if (!showPromotion) return 0;

    const basePrice = Number(item.product.ecomUnitPrice) || 0;

    const promos = activePromotions.filter(
      (p: any) =>
        p.promotionBy === "ByValue" &&
        typeof p.buyQty === "number" &&
        quantity >= p.buyQty,
    );

    if (!promos || promos.length == 0) return 0;

    let discounted = basePrice;

    for (const promo of promos) {
      if (promo.promotionType === "%") {
        discounted -= (basePrice * promo.promotion) / 100;
      }

      if (promo.promotionType === "INR") {
        discounted -= promo.promotion;
      }
    }

    return ((basePrice - discounted) / basePrice) * 100;
  }, [item.product.ecomUnitPrice, activePromotions, quantity, showPromotion]);

  //#endregion

  const basePrice = useMemo(
    () => Number(item.product.ecomUnitPrice) || 0,
    [item.product.ecomUnitPrice],
  );
  const discountedPrice = useMemo(
    () => calculateDiscountedPrice(),
    [calculateDiscountedPrice],
  );
  const discountedPercentage = useMemo(
    () => calculateDiscountedPercentage(),
    [calculateDiscountedPercentage],
  );

  // ISBN price: publisher/market list price; shown when higher than store's ecomUnitPrice
  const isbnPrice = useMemo(
    () => Number(item.product.isbnPrice) || 0,
    [item.product.isbnPrice],
  );
  const hasIsbnDiscount = isbnPrice > basePrice && discountedPrice >= basePrice;
  const isbnDiscountPercentage = hasIsbnDiscount
    ? Math.round(((isbnPrice - basePrice) / isbnPrice) * 100)
    : 0;
  const byValuePromotionMessage = useMemo(() => {
    if (!showPromotion) return "";

    const promos = activePromotions?.filter(
      (p: any) => typeof p.buyQty === "number",
    );

    if (!promos || promos.length == 0) return "";

    let str = "";
    let i = 0;
    for (let promo of promos) {
      if (quantity >= promo.buyQty) {
        str += `✅ Offer applied: ${promo.promotionName}`;
      } else {
        str += `Add ${promo.buyQty} quantities to unlock: ${promo.promotionName}`;
      }
      if (i < promos.length - 1) {
        str += " , ";
      }
      i++;
    }

    return str;
  }, [activePromotions, quantity, showPromotion]);

  const ShimmerOfferCard = () => (
    <div className="mt-3">
      <div className="flex gap-3 lg:gap-4">
        <div className="w-16 lg:w-20 flex-shrink-0" />
        <div className="-ml-20 lg:-ml-59 flex-1">
          <div className="p-3 rounded-md bg-gray-100 border border-gray-200 animate-pulse">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-4 h-4 bg-gray-300 rounded-full"></div>
              <div className="h-4 bg-gray-300 rounded w-24"></div>
            </div>

            {byQuantityPromotionProducts.map((promo, index) => (
              <div
                key={`shimmer-${promo.id}-${index}`}
                className="flex items-start gap-3 mt-3"
              >
                <div className="flex-shrink-0 w-16 h-20 lg:w-20 lg:h-28 bg-gray-300 rounded border border-gray-300"></div>

                <div className="min-w-0 flex-1">
                  <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>

                  <div className="flex gap-2 mb-2">
                    <div className="h-5 bg-gray-300 rounded w-16"></div>
                    <div className="h-5 bg-gray-300 rounded w-20"></div>
                  </div>

                  <div className="h-3 bg-gray-300 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div
      key={`${item.product.item}`}
      className="border-b border-vintageBorder pb-4 mb-6 last:border-b-0 last:mb-0"
    >
      <div className="flex gap-3 lg:gap-4">
        <Link to={`/product?pid=${encodeParam(id)}`} className="flex-shrink-0" onClick={() => { window.scrollTo(0, 0); onClose?.(); }}>
          <CartItemImage
             src={image || ""}
             alt={title}
             className="w-16 h-20 lg:w-20 lg:h-28 object-cover rounded border border-vintageBorder flex-shrink-0"
           />
        </Link>
 
         <div className="flex-1 min-w-0">
           <div className="flex justify-between items-start mb-2 gap-2">
             <div className="min-w-0 flex-1">
              {isCartMaxed && !isOutOfStock && (
                <div className="flex items-center gap-1 mt-1 mb-2">
                  <span className="inline-flex items-center text-[11px] font-semibold text-vintageText bg-vintageText/10 border border-vintageText/30 px-2 py-0.5 rounded-full">
                    You've already added all available quantity for this product
                  </span>
                </div>
              )}
              {!isOutOfStock && !isCartMaxed && stockQty > 0 && Math.max(stockQty - quantity, 0) > 0 && minInvQtyPerProduct > 0 && stockQty < minInvQtyPerProduct && (
                <div className="flex items-center gap-1 mt-1 mb-2">
                  <span className="text-[11px] font-semibold text-orange-700 bg-orange-50 border border-orange-200 px-2 py-0.5 rounded-full">
                    Only {Math.max(stockQty - quantity, 0)} of these are available in stock
                  </span>
                </div>
              )}
              {isStockLoading ? (
                <div className="flex items-center gap-1 mt-1 mb-2">
                  <div className="h-5 w-40 bg-gray-200 animate-pulse rounded-full" />
                </div>
              ) : isOutOfStock ? (
                <div className="flex items-center gap-2 mt-1 mb-2 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-center w-fit">
                  <span className="text-xs font-medium">
                    This product is currently unavailable
                  </span>
                </div>
              ) : null}
              <Link
                to={`/product?pid=${encodeParam(id)}`}
                onClick={() => { window.scrollTo(0, 0); onClose?.(); }}
                className="text-base lg:text-xl font-bold text-vintageText capitalize font-melodramaRegular tracking-widest leading-tight mb-1 truncate block hover:underline"
              >
                {title}
              </Link>
              {showPromotion && (
                (() => {
                  const byQuantityPromo = activePromotions?.filter(
                    (p: any) => p.promotionBy === "ByQuantity",
                  );

                  if (!byQuantityPromo || byQuantityPromo.length === 0) return null;

                  return (
                    <div className="flex flex-wrap">
                      {byQuantityPromo.map((e) => (
                        <div key={e.promotionId}>
                          <span className="inline-block text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded mb-1">
                            🎁 Buy {e.buyQty} Get {e.getQty} Free
                          </span>
                        </div>
                      ))}
                    </div>
                  );
                })()
              )}
              {author && (
                <p className="text-xs lg:text-base text-justify text-gray-600 truncate">
                  {author}
                </p>
              )}
            </div>
            {showDeleteIcon && (
              <button
                onClick={removeItem}
                disabled={isInteracting}
                className={`flex-shrink-0 text-dark hover:text-vintageText p-1 transition-opacity ${isInteracting ? "opacity-40 cursor-not-allowed pointer-events-none" : ""}`}
              >
                <X size={16} />
              </button>
            )}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
            {isOutOfStock ? null : showQuantitySelector ? (
              <div className="flex items-center gap-2">
                <span className="text-xs lg:text-base text-dark">Qty:</span>

                <div className="flex items-center border border-vintageBorder rounded">
                  <button
                    onClick={decreaseQuantity}
                    disabled={
                      isDecreasing ||
                      isIncreasing ||
                      quantity <= 1 ||
                      isOfferLoading
                    }
                    className={`p-1 lg:p-2 transition-colors ${
                      isDecreasing ||
                      isIncreasing ||
                      quantity <= 1 ||
                      isOfferLoading
                        ? "opacity-50 cursor-not-allowed bg-gray-100"
                        : "hover:bg-vintageBg"
                    }`}
                  >
                    {isDecreasing ? (
                      <div className="w-3 h-3 border border-vintageText border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Minus size={12} />
                    )}
                  </button>
                  <span className="px-2 py-1 text-xs lg:text-sm min-w-[30px] lg:min-w-[40px] text-center border-x border-vintageBorder">
                    {quantity}
                  </span>
                  <button
                    onClick={increaseQuantity}
                    disabled={
                      isIncreasing || isDecreasing || isOfferLoading || quantity >= maxCartQty || (allowLTAVAILINVQTY === 0 && stockQty > 0 && quantity >= stockQty)
                    }
                    className={`p-1 lg:p-2 transition-colors ${
                      isIncreasing || isDecreasing || isOfferLoading || quantity >= maxCartQty || (allowLTAVAILINVQTY === 0 && stockQty > 0 && quantity >= stockQty)
                        ? "opacity-50 cursor-not-allowed bg-gray-100"
                        : "hover:bg-vintageBg"
                    }`}
                  >
                    {isIncreasing ? (
                      <div className="w-3 h-3 border border-vintageText border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Plus size={12} />
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-xs lg:text-base text-dark">
                  Qty: {quantity}
                </span>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-3">
            {discountedPrice < basePrice ? (
              // Promotion-based discount takes priority
              <>
                <NumberFormatter
                  decimalScale={numberOfDecimal}
                  fixedDecimalScale
                  prefix={getBaseCurrency("INR") || undefined}
                  value={discountedPrice}
                  thousandSeparator
                  className="text-base lg:text-xl font-semibold text-vintageText"
                />
                <NumberFormatter
                  decimalScale={numberOfDecimal}
                  fixedDecimalScale
                  prefix={getBaseCurrency("INR") || undefined}
                  value={basePrice}
                  thousandSeparator
                  className="text-xs lg:text-lg text-dark line-through"
                />
                {discountedPercentage !== 0 && (
                  <span className="text-xs bg-green-100 text-green-800 px-1 py-0.5 rounded-full">
                    -{discountedPercentage.toFixed(0)}% OFF
                  </span>
                )}
              </>
            ) : hasIsbnDiscount ? (
              // ISBN price is higher than store price — show store price as discounted
              <div className="flex flex-col gap-0.5">
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-green-100 text-green-800 px-1 py-0.5 rounded-full font-semibold">
                    -{isbnDiscountPercentage.toFixed(0)}%
                  </span>
                  <NumberFormatter
                    decimalScale={numberOfDecimal}
                    fixedDecimalScale
                    prefix={getBaseCurrency("INR") || undefined}
                    value={basePrice}
                    thousandSeparator
                    className="text-base lg:text-xl font-semibold text-vintageText"
                  />
                </div>
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <span>M.R.P.:</span>
                  <NumberFormatter
                    decimalScale={numberOfDecimal}
                    fixedDecimalScale
                    prefix={getBaseCurrency("INR") || undefined}
                    value={isbnPrice}
                    thousandSeparator
                    className="line-through"
                  />
                </div>
              </div>
            ) : (
              <NumberFormatter
                decimalScale={numberOfDecimal}
                fixedDecimalScale
                prefix={getBaseCurrency("INR") || undefined}
                value={basePrice}
                thousandSeparator
                className="text-base lg:text-xl font-semibold text-vintageText"
              />
            )}
          </div>

          {byValuePromotionMessage && (
            <p className="text-xs text-orange-600 my-1">
              {byValuePromotionMessage}
            </p>
          )}

          {isOfferLoading ? (
            <ShimmerOfferCard />
          ) : showPromotion && byQuantityPromoProducts.length > 0 ? (
            <div className="mt-3">
              <div className="flex gap-3 lg:gap-4">
                <div className="w-16 lg:w-20 flex-shrink-0" />

                <div className="-ml-20 lg:-ml-59 flex-1">
                  <div className="p-3 rounded-md bg-green-50 border border-green-200">
                    <p className="text-xs font-semibold text-green-700 mb-2 flex items-center gap-1">
                      🎁 Offer Applied
                    </p>

                    {byQuantityPromoProducts.map((promoItem) => (
                      <div
                        key={promoItem.product.item}
                        className="flex items-start gap-3 mt-2"
                      >
                        <Link
                          to={`/product?pid=${encodeParam(promoItem.product.item)}`}
                          className="flex-shrink-0"
                        >
                          <CartItemImage
                            src={promoItem.imagePath || ""}
                            alt={promoItem.product.itemDesc}
                            className="w-16 h-20 lg:w-20 lg:h-28 object-cover rounded border border-vintageBorder flex-shrink-0"
                          />
                        </Link>

                        <div className="min-w-0 flex-1">
                          <Link
                            to={`/product?pid=${encodeParam(promoItem.product.item)}`}
                            onClick={() => window.scrollTo(0, 0)}
                            className="text-sm lg:text-base font-semibold text-vintageText truncate block hover:underline"
                          >
                            {promoItem.product.itemDesc}
                          </Link>
                          <div className="flex gap-2 mt-1">
                            <span className="inline-block text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded">
                              Free Item
                            </span>
                            <span className="inline-block text-xs font-semibold text-orange-700 bg-orange-100 px-2 py-0.5 rounded">
                              Qty:{" "}
                              {
                                byQuantityPromotionProducts.find(
                                  (p) => p.id == promoItem.product.item,
                                )?.qty
                              }
                            </span>
                          </div>

                          {promoItem.product.author && (
                            <p className="text-xs lg:text-sm text-gray-600 truncate mt-1">
                              {promoItem.product.author}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-t border-[#326638] pt-2 text-xs lg:text-sm text-dark leading-snug">
            <div className="flex items-center gap-2 text-gray-700"></div>
            <div className="flex items-center gap-2 text-gray-700"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(CartItemCard);
